import { GOODRX } from '../../../../constants';
import { replacePageMetaTemplatesWithVariable } from '../replacePageMetaTemplatesWithVariable';
const TITLE = 'title';
const DESCRIPTION = 'description';
const OBJECT = 'object';
/**
 * PageMetaModel
 * -------------
 * Page Meta Schema are populated by two types of variables:
 * - EDITOR: custom input fields the editor fills out in Contentful (in Page Meta)
 * - APP: a set of variables for the current page representing things like page reviewer, author, parent URL, etc
 * Usage:
 *
 * The current page uses this model for:
 * - populating all its schema items with both EDITOR and APP variables, to fill out head meta tags, AND JSON LD
 * Related pages use this model for:
 * - retrieving meta items (title and description). These pages only populate the meta items with EDITOR variables
 * (the app variables are not available for related pages)
 *
 * @param {PageMetaType} pageMeta Page Meta values for this current page, or for reach related page, including input (EDITOR) values
 * @param {PopulatePageMetaAppInputType} appInput: the APP variables for the current page (not used by related pages)
 */

export class PageMetaModel {
  constructor({
    pageMeta,
    appInput
  }) {
    var _this$sourceContext, _this$sourceContext$s;

    this.pageMeta = pageMeta;
    const {
      metaSchemaPageCollection
    } = pageMeta;
    this.sourceContext = metaSchemaPageCollection || pageMeta;
    this.metaItems = [];
    this.filledSchema = [];
    this.setMetaItems();
    this.schemaItems = (((_this$sourceContext = this.sourceContext) === null || _this$sourceContext === void 0 ? void 0 : (_this$sourceContext$s = _this$sourceContext.schemaItemsCollection) === null || _this$sourceContext$s === void 0 ? void 0 : _this$sourceContext$s.items) || []).filter(x => x !== null); // the current page can populate appInput with internal class instance page title and description
    // other JSON LD schema items can then use these meta values as APP variables

    if (appInput) {
      var _this$image;

      const extendedAppInput = {
        description: this.description || GOODRX,
        imageUrl: ((_this$image = this.image) === null || _this$image === void 0 ? void 0 : _this$image.url) || undefined,
        title: this.title || GOODRX,
        ...appInput
      };
      this.fillVariables(extendedAppInput);
    }
  }

  setMetaItems() {
    var _this$sourceContext2, _this$sourceContext2$;

    this.metaItems = (((_this$sourceContext2 = this.sourceContext) === null || _this$sourceContext2 === void 0 ? void 0 : (_this$sourceContext2$ = _this$sourceContext2.metaItemsCollection) === null || _this$sourceContext2$ === void 0 ? void 0 : _this$sourceContext2$.items) || []).filter(x => x !== null);
    /**
     * If a page doesn't have title or description we need to add empty objects for them so those tags are created by <HeadTags/>
     */

    if (!this.getMetaItem(TITLE)) {
      this.metaItems = [...this.metaItems, this.getEmptyMetaItem(TITLE)];
    }

    if (!this.getMetaItem(DESCRIPTION)) {
      this.metaItems = [...this.metaItems, this.getEmptyMetaItem(DESCRIPTION)];
    }
  }

  getMetaItem(which) {
    return this.metaItems.find(item => {
      var _item$data;

      return (item === null || item === void 0 ? void 0 : (_item$data = item.data) === null || _item$data === void 0 ? void 0 : _item$data.type) === which;
    });
  }

  getEmptyMetaItem(which) {
    return {
      data: {
        type: which,
        value: ''
      },
      type: OBJECT
    };
  }

  get titleItem() {
    return this.getMetaItem(TITLE);
  }

  get descriptionItem() {
    return this.getMetaItem(DESCRIPTION);
  }

  get image() {
    return this.pageMeta.image || undefined;
  }

  get title() {
    var _this$titleItem;

    return replacePageMetaTemplatesWithVariable({
      appInput: {},
      data: (_this$titleItem = this.titleItem) === null || _this$titleItem === void 0 ? void 0 : _this$titleItem.data,
      input: this.pageMeta.input
    }).value || '';
  }

  get description() {
    var _this$descriptionItem;

    return replacePageMetaTemplatesWithVariable({
      appInput: {},
      data: (_this$descriptionItem = this.descriptionItem) === null || _this$descriptionItem === void 0 ? void 0 : _this$descriptionItem.data,
      input: this.pageMeta.input
    }).value || '';
  } // eslint-disable-next-line


  fillVariables(appInput) {
    let allItems = [...this.schemaItems, ...this.metaItems];
    allItems = allItems.map(item => {
      const data = replacePageMetaTemplatesWithVariable({
        appInput,
        data: item.data,
        input: this.pageMeta.input
      });
      return { ...item,
        data
      };
    });
    this.filledSchema = allItems;
  }

  get breadcrumbs() {
    if (!this.filledSchema) {
      return [];
    }

    const breadcrumbList = this.filledSchema.find(schema => schema.data['@type'] === 'BreadcrumbList');
    return breadcrumbList ? // eslint-disable-next-line
    breadcrumbList.data.itemListElement.map(({
      item,
      name
    }) => ({
      item,
      name
    })) : [];
  }

}